import React, { useMemo, useCallback } from 'react';
import {
  createSearchPage,
  BaseSearchProps,
  createRequestParams,
  OnDateRangeFilter,
  ResetDateFilter,
} from '@erecruit/modules';

import { Modal, H1, styled, ant, Flex, Text, FlexItem } from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { Resources, ListParams } from '@erecruit/types';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import { defaultVisibleColumns, generateColumns } from './columns';
import {
  selectors,
  searchPositions,
  searchPositionShiftTypes,
} from '../../state/reducer';
import {
  parseTableFilters,
  PositionColumnType,
} from '../../../PositionSearch/columns';
import { addNewShiftWizardRoute } from '../../../../routes';

const { FormattedMessage } = Intl;

type PositionSearchFilterData = {};

export interface ShiftPositionSearchProps
  extends BaseSearchProps<
      Resources.PositionSearch,
      PositionSearchFilterData,
      Resources.PositionSearchFilter
    >,
    Intl.InjectedIntlProps {}

type ShiftPositionParams = 'All';

function getRequestParams(
  positionType: ShiftPositionParams,
): ListParams<Resources.PositionSearchFilter> {
  return createRequestParams(
    {
      positionSearchType: Resources.PositionSearchType[positionType],
    },
    nameof<Resources.PositionSearch>(o => o.createdOn),
  );
}

const useColumns = (
  searchParams: ListParams<Resources.PositionSearchFilter>,
  setSearchParams: React.Dispatch<
    React.SetStateAction<ListParams<Resources.PositionSearchFilter>>
  >,
) => {
  const filterByDate: OnDateRangeFilter = useCallback(
    (fieldKey, range) => {
      setSearchParams({
        ...searchParams,
        filter: { ...searchParams.filter, [fieldKey]: range },
      });
    },
    [searchParams, setSearchParams],
  );

  const resetDateFilter: ResetDateFilter = useCallback(
    dateFieldKey => {
      if (
        searchParams.filter &&
        !searchParams.filter[
          dateFieldKey as keyof Resources.PositionSearchFilter
        ]
      )
        return;
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [dateFieldKey as keyof Resources.PositionSearchFilter]: omit,
        ...other
      } = searchParams.filter as Resources.PositionSearchFilter;

      setSearchParams({
        ...searchParams,
        filter: { ...other },
      });
    },
    [searchParams, setSearchParams],
  );

  const filter = searchParams.filter;
  return useMemo(
    () => generateColumns(filterByDate, resetDateFilter, filter || {}),
    [filter, filterByDate, resetDateFilter],
  );
};

const ShiftPositionSearch = createSearchPage<
  Resources.PositionSearchFilter,
  ShiftPositionSearchProps,
  PositionColumnType,
  ShiftPositionParams,
  Resources.PositionSearch,
  PositionSearchFilterData
>({
  searchTermPlaceholderText: 'Search by positions',
  baseQueryRoute: 'positionType',
  defaultSearchQuery: 'All',
  alwaysVisibleColumns: defaultVisibleColumns,
  rowKey: nameof<Resources.PositionSearch>(o => o.id),
  getDefaultVisibleColumns: () => defaultVisibleColumns,
  useColumns,
  getRequestParams,
  useQueryParams: false,
  parseFilterParams: (filters, columns, filterParams) => {
    const parsedFilters = parseTableFilters(filters, columns);
    return {
      ...parsedFilters,
      positionSearchType: filterParams && filterParams.positionSearchType,
      searchTerm: filterParams && filterParams.searchTerm,
    };
  },
});

const Title = styled(H1)({
  marginBottom: 0,
});

const StyledModal = styled(Modal)(
  ant({
    '&.modal': {
      top: 100,
    },
    '.modal-body': {
      padding: '0 15px',
      maxHeight: '70vh',
    },
    '.modal-header': {
      borderBottom: 'none',
    },
  }),
);

const ModalShiftPositionSearch = (
  props: ShiftPositionSearchProps & {
    searchPositionShiftTypes: typeof searchPositionShiftTypes;
  },
) => {
  const history = useHistory();
  const location = useLocation();

  const onRowSelect = (record: Resources.PositionSearch) => {
    props.searchPositionShiftTypes(record.id);
    history.push({
      pathname: addNewShiftWizardRoute,
      // State is used to forward referer
      state: {
        referrer: location.state?.referrer,
      },
    });
  };

  return (
    <StyledModal
      closable={true}
      onCancel={() => history.push('/shifts')}
      footer={null}
      width={992}
      pageTitle={
        <Title color="headerTertiary">
          <FormattedMessage id={messageIds.vms.newShiftWizard.title} />
        </Title>
      }
      title={
        <Flex
          gutter={1}
          gutterDirection="vertical"
          direction="column"
          padding={[5, 0, 0, 0]}
        >
          <FlexItem>
            <Title>
              <FormattedMessage
                id={messageIds.vms.newShiftWizard.positionSearch.title}
              />
            </Title>
          </FlexItem>
          <FlexItem>
            <Text color="bodySecondary">
              <FormattedMessage
                id={messageIds.vms.newShiftWizard.positionSearch.subtitle}
              />
            </Text>
          </FlexItem>
        </Flex>
      }
      visible={true}
    >
      <ShiftPositionSearch
        style={{ maxHeight: '70vh' }}
        headerStyle={{ position: 'absolute', top: 10, right: 0 }}
        {...props}
        onRowSelect={onRowSelect}
      />
    </StyledModal>
  );
};

export default connect(selectors.selectSearchState, {
  searchEntities: searchPositions,
  searchPositionShiftTypes: searchPositionShiftTypes,
})(ModalShiftPositionSearch);
